// metric/groceryUtilsMetric.js - Metric measurement system utilities
import { normalizeIngredientName, getDensity } from '../groceryUtils';

// Define metric-specific unit conversions
export const METRIC_UNIT_CONVERSIONS = {
  weight: {
    baseUnit: "g",
    conversions: {
      kg: 1000,
      g: 1
    }
  },
  volume: {
    baseUnit: "ml",
    conversions: {
      l: 1000,
      ml: 1,
      tbsp: 15,
      tsp: 5,
      cup: 240
    }
  },
  count: {
    baseUnit: "piece",
    conversions: {
      piece: 1,
      pieces: 1,
      slice: 1,
      slices: 1,
      unit: 1,
      units: 1,
      whole: 1,
      bunch: 1,
      clove: 1,
      cloves: 1,
      leaf: 1,
      leaves: 1,
      sprig: 1,
      sprigs: 1,
      stalk: 1,
      stalks: 1,
      stem: 1,
      stems: 1,
      frond: 1,
      fronds: 1
    }
  }
};

/**
 * UNIFIED PART-TO-WHOLE CONVERSIONS
 * 
 * Products that are SOLD as whole units but often SPECIFIED as parts in recipes.
 * Example: garlic is sold as heads, but recipes ask for cloves
 * 
 * Structure:
 * - productMatchers: array of strings/regex to match product names
 * - partUnits: units that indicate "parts" (cloves, slices, leaves)
 * - wholeUnits: units that indicate "whole" products (piece, head, loaf)
 * - partsPerWhole: how many parts fit in one whole
 * - consolidationUnit: the unit to use for consolidation key
 * - fallbackThreshold: if unit is 'piece' but qty > threshold, treat as parts
 */
export const PART_TO_WHOLE_CONVERSIONS = {
  garlic: {
    productMatchers: ['garlic'],
    partUnits: ['clove', 'cloves'],
    wholeUnits: ['head', 'heads', 'piece', 'unit'],
    partsPerWhole: 10,  // 10 cloves per head
    consolidationUnit: 'clove',
    fallbackThreshold: 4,  // if >4 "pieces", likely cloves not heads
    logPrefix: '[Garlic]'
  },
  bread: {
    productMatchers: ['bread', 'toast', 'brioche', 'baguette'],
    partUnits: ['slice', 'slices'],
    wholeUnits: ['loaf', 'loaves', 'piece', 'unit'],
    partsPerWhole: 20,  // 20 slices per loaf
    consolidationUnit: 'slice',
    fallbackThreshold: 10,  // if >10 "pieces", likely slices
    logPrefix: '[Bread]'
  },
  basil: {
    productMatchers: ['basil', 'fresh basil'],
    partUnits: ['leaf', 'leaves'],
    wholeUnits: ['bunch', 'bunches', 'piece', 'unit'],
    partsPerWhole: 40,  // ~40 leaves per bunch
    consolidationUnit: 'leaf',
    fallbackThreshold: 10,  // if >10 "pieces", likely leaves
    logPrefix: '[Basil]'
  },
  parsley: {
    productMatchers: ['parsley', 'fresh parsley'],
    partUnits: ['sprig', 'sprigs', 'leaf', 'leaves'],
    wholeUnits: ['bunch', 'bunches', 'piece', 'unit'],
    partsPerWhole: 30,  // ~30 sprigs per bunch
    consolidationUnit: 'sprig',
    fallbackThreshold: 8,
    logPrefix: '[Parsley]'
  },
  cilantro: {
    productMatchers: ['cilantro', 'coriander', 'fresh cilantro', 'fresh coriander'],
    partUnits: ['sprig', 'sprigs', 'leaf', 'leaves'],
    wholeUnits: ['bunch', 'bunches', 'piece', 'unit'],
    partsPerWhole: 30,
    consolidationUnit: 'sprig',
    fallbackThreshold: 8,
    logPrefix: '[Cilantro]'
  },
  mint: {
    productMatchers: ['mint', 'fresh mint'],
    partUnits: ['leaf', 'leaves', 'sprig', 'sprigs'],
    wholeUnits: ['bunch', 'bunches', 'piece', 'unit'],
    partsPerWhole: 50,
    consolidationUnit: 'leaf',
    fallbackThreshold: 15,
    logPrefix: '[Mint]'
  },
  dill: {
    productMatchers: ['dill', 'fresh dill'],
    partUnits: ['sprig', 'sprigs', 'frond', 'fronds'],
    wholeUnits: ['bunch', 'bunches', 'piece', 'unit'],
    partsPerWhole: 25,
    consolidationUnit: 'sprig',
    fallbackThreshold: 8,
    logPrefix: '[Dill]'
  },
  chives: {
    productMatchers: ['chives', 'fresh chives'],
    partUnits: ['stalk', 'stalks', 'stem', 'stems'],
    wholeUnits: ['bunch', 'bunches', 'piece', 'unit'],
    partsPerWhole: 20,
    consolidationUnit: 'stalk',
    fallbackThreshold: 10,
    logPrefix: '[Chives]'
  },
  spring_onion: {
    productMatchers: ['spring onion', 'spring onions', 'green onion', 'green onions', 'scallion', 'scallions'],
    partUnits: ['stalk', 'stalks', 'stem', 'stems'],
    wholeUnits: ['bunch', 'bunches', 'piece', 'unit'],
    partsPerWhole: 6,  // typically 6 stalks per bunch
    consolidationUnit: 'piece',  // spring onions often sold individually
    fallbackThreshold: null,  // no fallback needed
    logPrefix: '[SpringOnion]'
  },
  vine_tomato: {
    productMatchers: ['vine tomato', 'vine tomatoes', 'tomatoes on the vine'],
    partUnits: ['piece', 'pieces', 'unit', 'units'],  // individual tomatoes
    wholeUnits: [],  // sold by weight, not whole units
    partsPerWhole: null,  // convert to weight instead
    avgWeightPerPart: 120,  // grams per tomato
    consolidationUnit: 'piece',
    fallbackThreshold: 2,  // if >2 tomatoes, convert to grams
    convertToWeight: true,
    logPrefix: '[VineTomato]'
  },
  lettuce: {
    productMatchers: ['lettuce', 'romaine', 'iceberg', 'butterhead', 'little gem', 'escarole'],
    partUnits: ['leaf', 'leaves'],
    wholeUnits: ['head', 'heads', 'piece', 'unit', 'pieces', 'units'],
    partsPerWhole: 20,  // ~20 usable leaves per head
    consolidationUnit: 'leaf',
    fallbackThreshold: 6,  // if >6 "pieces", likely leaves not heads
    logPrefix: '[Lettuce]'
  },
  cabbage: {
    productMatchers: ['cabbage', 'red cabbage', 'savoy cabbage', 'napa cabbage'],
    partUnits: ['leaf', 'leaves'],
    wholeUnits: ['head', 'heads', 'piece', 'unit', 'pieces', 'units'],
    partsPerWhole: 15,  // ~15 usable leaves per head
    consolidationUnit: 'leaf',
    fallbackThreshold: 4,
    logPrefix: '[Cabbage]'
  }
};

/**
 * Find the part-to-whole conversion config for a product
 * @param {string} productName - Normalized product name
 * @returns {Object|null} Conversion config or null if not found
 */
export const getPartToWholeConfig = (productName) => {
  const nameLower = productName.toLowerCase();
  
  for (const [key, config] of Object.entries(PART_TO_WHOLE_CONVERSIONS)) {
    for (const matcher of config.productMatchers) {
      if (nameLower === matcher || nameLower.includes(matcher)) {
        return { key, ...config };
      }
    }
  }
  return null;
};

/**
 * Check if a unit is a "part" unit for this product
 * @param {string} unit - The unit to check
 * @param {Object} config - The part-to-whole config
 * @returns {boolean}
 */
export const isPartUnit = (unit, config) => {
  if (!config || !unit) return false;
  const unitLower = unit.toLowerCase();
  return config.partUnits.includes(unitLower);
};

/**
 * Get the consolidation unit for a product (for consistent keying)
 * @param {string} productName - Normalized product name
 * @param {string} currentUnit - Current unit
 * @returns {string} The unit to use for consolidation key
 */
export const getConsolidationUnit = (productName, currentUnit) => {
  const config = getPartToWholeConfig(productName);
  if (!config) return currentUnit;
  
  const unitLower = currentUnit.toLowerCase();
  
  // If it's a part unit, use the consolidation unit
  if (config.partUnits.includes(unitLower)) {
    return config.consolidationUnit;
  }
  
  // If it's a whole unit (piece, unit, etc.), still use consolidation unit for consistent keying
  if (config.wholeUnits.includes(unitLower) || unitLower === 'piece' || unitLower === 'unit') {
    return config.consolidationUnit;
  }
  
  return currentUnit;
};

/**
 * Normalize a part unit to its standard form (for consolidation)
 * @param {string} productName - Normalized product name  
 * @param {string} unit - Current unit
 * @param {number} qty - Current quantity
 * @returns {Object} { normalizedUnit, qty, isPart, config }
 */
export const normalizePartUnit = (productName, unit, qty) => {
  const config = getPartToWholeConfig(productName);
  if (!config) {
    return { normalizedUnit: unit, qty, isPart: false, config: null };
  }
  
  const unitLower = unit.toLowerCase();
  
  // If it's already a part unit, normalize to consolidation unit
  if (config.partUnits.includes(unitLower)) {
    console.log(`${config.logPrefix} Normalizing ${qty} ${unit} → ${config.consolidationUnit}`);
    return { 
      normalizedUnit: config.consolidationUnit, 
      qty, 
      isPart: true, 
      config 
    };
  }
  
  // If it's a whole unit (piece, unit), check if qty suggests it's actually parts
  if (config.wholeUnits.includes(unitLower) || unitLower === 'piece' || unitLower === 'unit') {
    if (config.fallbackThreshold && qty > config.fallbackThreshold) {
      console.log(`${config.logPrefix} Detected ${qty} "${unit}" exceeds threshold ${config.fallbackThreshold} → treating as ${config.consolidationUnit}`);
      return { 
        normalizedUnit: config.consolidationUnit, 
        qty, 
        isPart: true, 
        config,
        wasFallback: true
      };
    }
  }
  
  return { normalizedUnit: unit, qty, isPart: false, config };
};

/**
 * Post-consolidation: convert parts to wholes
 * @param {Object} item - Consolidated ingredient item { name, totalQty, unit, ... }
 * @returns {Object} Modified item with converted quantity
 */
export const convertPartsToWholes = (item) => {
  const config = getPartToWholeConfig(item.name);
  if (!config) {
    console.log(`[convertPartsToWholes] No config for "${item.name}"`);
    return item;
  }
  
  console.log(`[convertPartsToWholes] Found config for "${item.name}":`, {
    key: config.key,
    partUnits: config.partUnits,
    consolidationUnit: config.consolidationUnit,
    partsPerWhole: config.partsPerWhole,
    itemUnit: item.unit,
    itemTotalQty: item.totalQty
  });
  
  const unitLower = item.unit.toLowerCase();
  const isPartUnitMatch = config.partUnits.includes(unitLower) || unitLower === config.consolidationUnit;
  
  console.log(`[convertPartsToWholes] isPartUnitMatch: ${isPartUnitMatch} (unitLower: "${unitLower}")`);
  
  // Check if we need to convert
  if (!isPartUnitMatch) {
    // Check fallback threshold for "piece" units
    if (unitLower === 'piece' && config.fallbackThreshold && item.totalQty > config.fallbackThreshold) {
      console.log(`[convertPartsToWholes] Fallback triggered: ${item.totalQty} > ${config.fallbackThreshold}`);
      // Continue to conversion below
    } else {
      console.log(`[convertPartsToWholes] Skipping conversion - unit not a part unit`);
      return item;
    }
  }
  
  // Special case: convert to weight instead of count
  if (config.convertToWeight && config.avgWeightPerPart) {
    const originalParts = item.totalQty;
    const totalGrams = Math.ceil(originalParts * config.avgWeightPerPart);
    console.log(`${config.logPrefix} POST-CONSOLIDATION: Converted ${originalParts} parts → ${totalGrams}g`);
    return {
      ...item,
      totalQty: totalGrams,
      unit: 'g',
      measurementType: 'weight'
    };
  }
  
  // Normal case: convert parts to wholes
  if (config.partsPerWhole) {
    const originalParts = item.totalQty;
    const wholeUnits = Math.ceil(originalParts / config.partsPerWhole);
    console.log(`${config.logPrefix} POST-CONSOLIDATION: Converted ${originalParts} ${config.consolidationUnit}(s) → ${wholeUnits} whole(s)`);
    return {
      ...item,
      totalQty: wholeUnits,
      unit: 'piece',
      measurementType: 'count'
    };
  }
  
  return item;
};

/**
 * List of dry ingredients (spices, powders) that should convert tsp/tbsp to grams, not ml
 * For these ingredients, we use approximate density to convert volume to weight
 */
const DRY_INGREDIENT_PATTERNS = [
  // Spices
  'cumin', 'cinnamon', 'paprika', 'smoked paprika', 'sweet paprika', 'turmeric', 'coriander', 'ginger',
  'nutmeg', 'cardamom', 'cloves', 'allspice', 'cayenne', 'chili',
  'pepper', 'black pepper', 'white pepper', 'mixed pepper', 'ground pepper', 'peppercorn',
  'oregano', 'basil', 'thyme', 'rosemary', 'sage', 'parsley',
  'garlic powder', 'onion powder', 'curry', 'garam masala',
  // Flours & Powders
  'flour', 'baking powder', 'baking soda', 'cocoa', 'cornstarch',
  // Sugars
  'sugar', 'brown sugar', 'powdered sugar',
  // Salts
  'salt', 'sea salt', 'kosher salt',
  // Other dry goods
  'yeast', 'gelatin', 'chia', 'flax'
];

/**
 * Approximate densities for tsp/tbsp to gram conversion (g per ml)
 * Most spices are around 0.5-0.7 g/ml
 */
const DRY_INGREDIENT_DENSITIES = {
  'cumin': 0.55,
  'cinnamon': 0.56,
  'paprika': 0.55,
  'turmeric': 0.65,
  'coriander': 0.40,
  'ginger': 0.50,
  'oregano': 0.30,
  'basil': 0.25,
  'thyme': 0.35,
  'pepper': 0.50,
  'black pepper': 0.50,
  'white pepper': 0.50,
  'salt': 1.20,
  'sugar': 0.85,
  'flour': 0.55,
  'cocoa': 0.50,
  'default': 0.50  // Default for unknown dry ingredients
};

/**
 * Check if ingredient is a dry ingredient that should use weight instead of volume
 */
export const isDryIngredient = (ingredientName) => {
  const nameLower = ingredientName.toLowerCase();
  return DRY_INGREDIENT_PATTERNS.some(pattern => nameLower.includes(pattern));
};

/**
 * Get density for dry ingredient (g per ml)
 */
export const getDryIngredientDensity = (ingredientName) => {
  const nameLower = ingredientName.toLowerCase();
  for (const [key, density] of Object.entries(DRY_INGREDIENT_DENSITIES)) {
    if (nameLower.includes(key)) {
      return density;
    }
  }
  return DRY_INGREDIENT_DENSITIES['default'];
};

/**
 * Normalize an ingredient quantity to base units in metric system
 * @param {number} value - Quantity value
 * @param {string} unit - Unit of measurement
 * @param {string} ingredientName - Name of ingredient (for density conversions)
 * @returns {Object} Normalized value and unit in metric base units
 */
export const normalizeToMetricBase = (value, unit, ingredientName = '') => {
  if (!unit) return { value, unit: 'piece', measurementType: 'count' };
  
  const unitLower = unit.toLowerCase().trim();
  
  // Handle each measurement type
  // Weight units
  if (METRIC_UNIT_CONVERSIONS.weight.conversions[unitLower]) {
    const factor = METRIC_UNIT_CONVERSIONS.weight.conversions[unitLower];
    return { 
      value: value * factor, 
      unit: 'g', 
      measurementType: 'weight', 
      originalUnit: unit 
    };
  }
   
  // Volume units
  if (METRIC_UNIT_CONVERSIONS.volume.conversions[unitLower]) {
    const factor = METRIC_UNIT_CONVERSIONS.volume.conversions[unitLower];
    const volumeInMl = value * factor;
    
    // SPECIAL HANDLING: For dry ingredients (spices, powders) with tsp/tbsp,
    // convert to weight (grams) instead of volume (ml)
    if ((unitLower === 'tsp' || unitLower === 'tbsp') && isDryIngredient(ingredientName)) {
      const density = getDryIngredientDensity(ingredientName);
      const weightInGrams = Math.round(volumeInMl * density * 10) / 10; // Round to 1 decimal
      console.log(`[DryIngredient] Converting ${value} ${unit} ${ingredientName} → ${weightInGrams}g (density: ${density})`);
      return { 
        value: weightInGrams, 
        unit: 'g', 
        measurementType: 'weight', 
        originalUnit: unit 
      };
    }
    
    return { 
      value: volumeInMl, 
      unit: 'ml', 
      measurementType: 'volume', 
      originalUnit: unit 
    };
  }
  
  // Count units
  if (METRIC_UNIT_CONVERSIONS.count.conversions[unitLower]) {
    // Special handling: keep clove and slice as distinct units for later conversion
    // This allows us to consolidate all cloves/slices first, then convert to heads/loaves
    let finalUnit = 'piece';
    if (unitLower === 'clove' || unitLower === 'cloves') {
      finalUnit = 'clove'; // Keep as clove for garlic consolidation
    } else if (unitLower === 'slice' || unitLower === 'slices') {
      finalUnit = 'slice'; // Keep as slice for bread consolidation
    } else if (unitLower === 'leaf' || unitLower === 'leaves') {
      finalUnit = 'leaf'; // Keep as leaf for basil/herb consolidation
    }
    
    return { 
      value, 
      unit: finalUnit, 
      measurementType: 'count', 
      originalUnit: unit 
    };
  }
  
  // Default handling if unrecognized
  console.warn(`Unrecognized unit: ${unit}, defaulting to piece`);
  return { value, unit: 'piece', measurementType: 'count', originalUnit: unit };
};

/**
 * Convert between volume and weight units in metric system
 * @param {number} value - Quantity value
 * @param {string} fromUnit - Source unit (ml or g)
 * @param {string} toUnit - Target unit (ml or g)
 * @param {string} ingredientName - Ingredient name for density lookup
 * @returns {number|null} Converted value or null if conversion not possible
 */
export const convertVolumeWeightMetric = (value, fromUnit, toUnit, ingredientName) => {
  if (fromUnit === toUnit) return value;
  
  // Only handle g <-> ml conversions
  if ((fromUnit === 'g' && toUnit === 'ml') || (fromUnit === 'ml' && toUnit === 'g')) {
    // Use getDensity to get density from PRODUCT_METADATA
    const density = getDensity(ingredientName);
    
    if (!density) {
      console.warn(`No density information for ${ingredientName}, cannot convert between weight and volume`);
      return null;
    }
    
    // g to ml: divide by density
    if (fromUnit === 'g' && toUnit === 'ml') {
      return value / density;
    }
    
    // ml to g: multiply by density
    if (fromUnit === 'ml' && toUnit === 'g') {
      return value * density;
    }
  }
  
  console.warn(`Cannot convert between ${fromUnit} and ${toUnit} in metric system`);
  return null;
};

/**
 * Find the best package size to purchase (metric)
 * @param {Array} standardSizes - Available package sizes
 * @param {number} requiredAmount - Required amount in base units
 * @param {string} baseUnit - Base unit (g, ml, piece)
 * @returns {Object} Best package option
 */
export const pickBestPackageMetric = (standardSizes, requiredAmount, baseUnit) => {
  if (!Array.isArray(standardSizes) || standardSizes.length === 0) {
    throw new Error('No standard sizes available');
  }
  
  // Sort sizes by amount
  const sortedSizes = [...standardSizes].sort((a, b) => {
    // Convert all to base unit if needed
    const aAmount = normalizeToMetricBase(a.amount, a.unit).value;
    const bAmount = normalizeToMetricBase(b.amount, b.unit).value;
    return aAmount - bAmount;
  });
  
  // Find first package that meets or exceeds requirement
  const sufficientPackage = sortedSizes.find(pkg => {
    const pkgAmount = normalizeToMetricBase(pkg.amount, pkg.unit).value;
    return pkgAmount >= requiredAmount;
  });
  
  // If found a sufficient package, return it
  if (sufficientPackage) return sufficientPackage;
  
  // Otherwise return the largest available package
  return sortedSizes[sortedSizes.length - 1];
};

/**
 * Calculate total cost with proper formatting
 * @param {Array} groceryList - List of grocery items
 * @param {String} currencySymbol - Currency symbol to use
 * @returns {String} Formatted total cost
 */
export const calculateTotalCostMetric = (groceryList, currencySymbol) => {
  const total = groceryList.reduce((sum, item) => {
    if (!item.totalPrice) return sum;
    
    const priceMatch = String(item.totalPrice).match(/(\d+(\.\d+)?)/);
    if (priceMatch) {
      return sum + parseFloat(priceMatch[1]);
    }
    return sum;
  }, 0);
  
  return `${currencySymbol}${total.toFixed(2)}`;
};

/**
 * Organize products by category
 * @param {Array} groceryList - Grocery list items
 * @returns {Array} Categorized product groups
 */
export const categorizeProductsMetric = (groceryList) => {
  const categories = {};
  
  groceryList.forEach(item => {
    const category = item.category || 'Other';
    if (!categories[category]) {
      categories[category] = [];
    }
    categories[category].push(item);
  });
  
  return Object.entries(categories).map(([category, items]) => ({
    category,
    items
  }));
};